import React, { Suspense } from 'react';
import { resolveEmoji } from '@/lib/emojiRegistry';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { emitAppEvent } from '@/lib/appEvents';
import { useTranslation } from 'react-i18next';

// CodeBlock + prism-react-renderer are heavy (~86 kB / 27 kB gzip). Most
// messages don't contain code blocks, so we keep the entire syntax-highlight
// path out of the eager bundle and only pull it in when the renderer
// actually hits a triple-backtick fence.
const CodeBlock = lazyWithReload(() => import('@/lib/CodeBlock'));
function CodeBlockFallback(props: { code: string }) {
  // Minimal pre/code so a plain copy of the code still reads while the
  // chunk fetches. No styling beyond the existing pre defaults; the swap
  // is usually invisible because the chunk lands within a frame.
  return (
    <pre className="my-1 overflow-x-auto rounded-md bg-black/20 px-3 py-2 text-[12px] leading-snug">
      <code className="whitespace-pre font-mono">{props.code}</code>
    </pre>
  );
}

/**
 * Tiny chat markdown renderer. Discord-style subset:
 *
 *   **bold**        *italic*        ~~strike~~     __underline__
 *   `inline code`   ```code block```                > blockquote
 *   http(s)://… auto-links            [label](url) explicit link
 *
 * Why custom, not `react-markdown` / `marked`:
 *   - CommonMark is overkill for chat (no tables, headings, lists, images)
 *   - Smaller bundle, predictable output, no innerHTML escape hatches
 *   - Discord doesn't use CommonMark either — they ship a subset like this
 *
 * Safety: this never produces raw HTML. We build React elements directly,
 * so user content can't inject script / markup. URLs are validated to be
 * http(s) before being rendered as <a>.
 */

/* eslint-disable react-refresh/only-export-components */

export function renderMarkdown(content: string): React.ReactNode {
  return renderBlocks(content);
}

// ---------------------------------------------------------------------------
// Block pass — split by line, group fenced code and blockquotes
// ---------------------------------------------------------------------------

function renderBlocks(content: string): React.ReactNode {
  const lines = content.split('\n');
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Fenced code block: ```lang ... ``` (language hint after fence opener)
    if (line.trimStart().startsWith('```')) {
      // Capture language hint: the text after ``` on the opening line.
      // Used to feed prism-react-renderer; falls back to plain "text"
      // when nothing or unknown is given.
      const lang = line.trimStart().slice(3).trim().toLowerCase();
      const codeLines: string[] = [];
      let j = i + 1;
      while (j < lines.length) {
        const candidate = lines[j] ?? '';
        if (candidate.trimStart().startsWith('```')) break;
        codeLines.push(candidate);
        j++;
      }
      if (j < lines.length) {
        const code = codeLines.join('\n');
        nodes.push(
          <Suspense key={`b${key++}`} fallback={<CodeBlockFallback code={code} />}>
            <CodeBlock code={code} language={lang} />
          </Suspense>,
        );
        i = j + 1;
        continue;
      }
    }

    // Blockquote — group consecutive `> …` lines
    if (line.startsWith('> ') || line === '>') {
      const quoteLines: string[] = [];
      while (i < lines.length) {
        const candidate = lines[i] ?? '';
        if (!candidate.startsWith('> ') && candidate !== '>') break;
        quoteLines.push(candidate === '>' ? '' : candidate.slice(2));
        i++;
      }
      nodes.push(
        <blockquote
          key={`b${key++}`}
          // Cyan-tinted left border + dim text — reads as "quoted" on the
          // dark message surface. Both bubbles share the dark surface
          // now, so a single treatment works for own + foreign.
          className="my-0.5 border-l-2 border-accent/50 pl-2 text-ink-dim"
        >
          {renderInline(quoteLines.join('\n'))}
        </blockquote>,
      );
      continue;
    }

    // Regular line — render inline, then a soft break. Empty lines just
    // produce vertical space (no <br/> needed thanks to whitespace-pre-line
    // semantics on the parent).
    nodes.push(
      <React.Fragment key={`b${key++}`}>
        {renderInline(line)}
        {i < lines.length - 1 && '\n'}
      </React.Fragment>,
    );
    i++;
  }

  return <span className="whitespace-pre-wrap break-words">{nodes}</span>;
}

// ---------------------------------------------------------------------------
// Inline pass — walk the string, emit React for the earliest match
// ---------------------------------------------------------------------------

interface InlineRule {
  /** Matches in priority order. First non-greedy delimiter wins. */
  pattern: RegExp;
  /** Renders the matched content. The body argument is m[1] (or m[1]+m[2]
   * for the explicit-link rule that has two groups). Pre-extracted to keep
   * the call site clean and TS-strict-friendly. */
  render: (groups: string[], key: string) => React.ReactNode;
}

const INLINE_RULES: InlineRule[] = [
  // Inline code — eats anything between matched backticks, including * etc.
  // Process FIRST so emphasis inside backticks doesn't get parsed.
  // bg-bg + thin ring reads as an inset chip on the dark message bubble.
  {
    pattern: /`([^`\n]+?)`/,
    render: ([body], key) => (
      <code key={key} className="rounded bg-bg px-1 py-0.5 font-mono text-[12px] text-ink">
        {body}
      </code>
    ),
  },
  // [label](url) — explicit link
  {
    pattern: /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/,
    render: ([label, href], key) => (
      <ExternalLink key={key} href={href ?? '#'}>
        {label}
      </ExternalLink>
    ),
  },
  // <https://…> — angle-bracket link (Discord pattern, mutes preview)
  {
    pattern: /<(https?:\/\/[^\s>]+)>/,
    render: ([href], key) => (
      <ExternalLink key={key} href={href ?? '#'}>
        {href}
      </ExternalLink>
    ),
  },
  // Auto-link bare URL. Trailing punctuation excluded so periods at the
  // end of sentences don't get glued into the URL.
  {
    pattern: /(https?:\/\/[^\s<>"'`)]+[^\s<>"'`).,!?:;])/,
    render: ([href], key) => (
      <ExternalLink key={key} href={href ?? '#'}>
        {href}
      </ExternalLink>
    ),
  },
  // **bold** — runs before *italic* so the longer delimiter wins
  {
    pattern: /\*\*([^*\n]+?)\*\*/,
    render: ([body], key) => (
      <strong key={key} className="font-semibold">
        {renderInline(body ?? '')}
      </strong>
    ),
  },
  // __underline__ — Discord uses this; underscores inside words are
  // protected by requiring the closing __ to match. decoration-current
  // so the underline takes the bubble's text color.
  {
    pattern: /__([^_\n]+?)__/,
    render: ([body], key) => (
      <u key={key} className="underline decoration-current/60 underline-offset-2">
        {renderInline(body ?? '')}
      </u>
    ),
  },
  // *italic* — single asterisks
  {
    pattern: /\*([^*\n]+?)\*/,
    render: ([body], key) => (
      <em key={key} className="italic">
        {renderInline(body ?? '')}
      </em>
    ),
  },
  // _italic_ — the form people actually type (Discord and Telegram both take
  // it). Runs AFTER __underline__ so a double underscore still wins, and is
  // boundary-guarded so snake_case identifiers survive as plain text.
  {
    pattern: /(?<![\w*_])_([^_\n]+?)_(?![\w*_])/,
    render: ([body], key) => (
      <em key={key} className="italic">
        {renderInline(body ?? '')}
      </em>
    ),
  },
  // ~~strike~~. Opacity dims it without breaking on accent-blue bubbles
  // where text-ink-dim disappears against the background.
  {
    pattern: /~~([^~\n]+?)~~/,
    render: ([body], key) => (
      <del key={key} className="opacity-70">
        {renderInline(body ?? '')}
      </del>
    ),
  },
  // Spoiler — `||hidden||` (Discord syntax). Renders as a clickable
  // blur that reveals on click. Used by the /spoiler command and any
  // user who types the syntax directly.
  {
    pattern: /\|\|([^|\n]{1,400})\|\|/,
    render: ([body], key) => (
      <Spoiler key={key}>{body ?? ''}</Spoiler>
    ),
  },
  // Group mentions: @here / @everyone / @<role-name>. Order matters —
  // these need to match BEFORE the generic @username rule below or the
  // word-boundary rule would eat them as plain usernames.
  // - @here / @everyone get an orange-secondary tint (Marcus secondary
  //   accent) so they read as "loud" pings vs the navy single-user
  //   mentions.
  // - @&<role-name> matches role pings (Discord syntax variant); we
  //   render the chip with the role-color when present (rendered
  //   server-side as data-attribute), fallback to orange.
  {
    pattern: /(^|[^\w])@(here|everyone|all)\b/i,
    render: ([leading, target], key) => (
      <React.Fragment key={key}>
        {leading}
        <span
          className="md-mention rounded font-semibold"
          style={{
            backgroundColor: 'rgb(var(--bk-secondary) / 0.18)',
            color: 'rgb(var(--bk-secondary))',
            padding: '0 4px',
          }}
        >
          @{(target ?? 'here').toLowerCase()}
        </span>
      </React.Fragment>
    ),
  },
  // Role mention: @&RoleName (Discord-flavored syntax adapted). Lets
  // moderators @&Owner draft, @&Captains stand-up etc. Style matches
  // generic mention but with a hash-derived color so different roles
  // visually differ in the same message stream.
  {
    pattern: /(^|[^\w])@&([a-zA-Z0-9_.\- ]{1,64})/,
    render: ([leading, role], key) => (
      <React.Fragment key={key}>
        {leading}
        <span
          className="md-mention rounded font-medium"
          style={{
            backgroundColor: 'rgb(var(--bk-accent) / 0.18)',
            color: 'rgb(var(--bk-accent-soft))',
            padding: '0 4px',
          }}
        >
          @{(role ?? '').trim()}
        </span>
      </React.Fragment>
    ),
  },
  // @username mention. We don't validate against the member list here —
  // the server resolves the actual mention IDs; rendering an unmatched
  // @foo as a styled chip is fine (and matches Discord behaviour).
  {
    pattern: /(^|[^\w])@([a-zA-Z0-9_.-]{1,64})/,
    render: ([leading, username], key) => (
      <React.Fragment key={key}>
        {leading}
        <span
          role="button"
          tabIndex={0}
          className="md-mention cursor-pointer rounded bg-accent/15 px-1 font-medium text-accent transition-colors hover:bg-accent/30"
          onClick={(e) => {
            e.stopPropagation();
            emitAppEvent('mention:open', { username: username ?? '' });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              emitAppEvent('mention:open', { username: username ?? '' });
            }
          }}
        >
          @{username}
        </span>
      </React.Fragment>
    ),
  },
  // Custom server emoji — :name:. We resolve against the
  // emojiRegistry (set when the user enters a guild). Unmatched
  // shortcodes fall through to plain text via the literal render path,
  // because returning null from `render` would still consume the
  // characters; we keep them visible by emitting them as-is.
  {
    pattern: /:([a-z0-9_]{2,32}):/,
    render: ([name], key) => {
      const e = resolveEmoji(name ?? '');
      if (!e) {
        return <React.Fragment key={key}>:{name}:</React.Fragment>;
      }
      return (
        <img
          key={key}
          src={e.imageUrl}
          alt={`:${e.name}:`}
          title={`:${e.name}:`}
          className="inline-block h-[1.25em] w-[1.25em] -translate-y-[1px] align-middle"
          draggable={false}
        />
      );
    },
  },
];

function renderInline(text: string): React.ReactNode {
  if (!text) return null;
  const out: React.ReactNode[] = [];
  let remaining = text;
  let cursor = 0;
  let k = 0;

  // Walk: find the earliest match of any rule, emit the text before it,
  // emit the React element, repeat. If nothing matches, dump the rest.
  while (remaining.length > 0) {
    let bestIdx = Infinity;
    let bestRule: InlineRule | null = null;
    let bestMatch: RegExpExecArray | null = null;

    for (const rule of INLINE_RULES) {
      const m = rule.pattern.exec(remaining);
      if (m && m.index < bestIdx) {
        bestIdx = m.index;
        bestRule = rule;
        bestMatch = m;
      }
    }

    if (!bestRule || !bestMatch) {
      out.push(remaining);
      break;
    }

    if (bestIdx > 0) out.push(remaining.slice(0, bestIdx));
    // Slice off the full-match group and pass only the capture groups —
    // the render fns destructure positionally, so trim the head.
    const groups = bestMatch.slice(1);
    out.push(bestRule.render(groups, `i${cursor + bestIdx}-${k++}`));
    const consumed = bestIdx + bestMatch[0].length;
    remaining = remaining.slice(consumed);
    cursor += consumed;
  }

  return out;
}

/**
 * Strip markdown delimiters for plain-text previews (reply quotes, search
 * results, system notifications). Keeps the visible text, drops the syntax.
 */
export function stripMarkdown(content: string): string {
  return content
    // Drop fenced code blocks entirely — they don't render well in one-liners.
    .replace(/```[\s\S]*?```/g, ' ')
    // Inline code: keep the body without backticks.
    .replace(/`([^`\n]+?)`/g, '$1')
    // Explicit [label](url): keep the label.
    .replace(/\[([^\]\n]+)\]\(https?:\/\/[^\s)]+\)/g, '$1')
    // Angle-bracket links and bare URLs: keep the URL text.
    .replace(/<(https?:\/\/[^\s>]+)>/g, '$1')
    // bold / underline / italic / strike — unwrap.
    .replace(/\*\*([^*\n]+?)\*\*/g, '$1')
    .replace(/__([^_\n]+?)__/g, '$1')
    .replace(/\*([^*\n]+?)\*/g, '$1')
    .replace(/~~([^~\n]+?)~~/g, '$1')
    .replace(/(?<![\w*_])_([^_\n]+?)_(?![\w*_])/g, '$1')
    // Spoilers stay HIDDEN, not unwrapped: a pinned banner or a chat-list
    // row must not leak what someone deliberately put behind a tap.
    .replace(/\|\|([^|\n]{1,400})\|\|/g, '▮▮▮')
    // Blockquote line prefix.
    .replace(/^>\s?/gm, '')
    .trim();
}

/**
 * Spoiler — Discord ||hidden|| syntax. Renders as an opaque pill that
 * reveals the wrapped text on click. Mouse cursor hints clickability;
 * keyboard users can focus + Enter. Once revealed, stays revealed
 * (matching Discord behaviour — re-hide would surprise the user).
 */
function Spoiler(props: { children: string }) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = React.useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setRevealed(true);
      }}
      aria-label={revealed ? undefined : t('messages.spoilerReveal')}
      className={
        'inline-block rounded px-1 align-baseline transition-colors ' +
        (revealed
          ? 'bg-ink/10 text-ink'
          : 'cursor-pointer bg-ink/85 text-ink/85 select-none hover:bg-ink/75')
      }
    >
      {props.children}
    </button>
  );
}

function ExternalLink(props: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={props.href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="md-link text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
    >
      {props.children}
    </a>
  );
}

// CodeBlock now lives in ./CodeBlock.tsx and is lazy-loaded; see the
// React.lazy() import at the top of this file. Keeping the import path
// stable means the rest of the renderer didn't need to change.
